import { useLazyGetCertificatesExpiryQuery } from '@/api'
import type { CertificateCheckInfo } from '@/api/models/medcomplex'
import { useModal } from '@/hooks'
import { ModalType, setEmployeesImportNotification } from '@/store/app'
import {
  Badge,
  Box,
  IconButton,
  Popover,
  Stack,
  ToggleButton,
  ToggleButtonGroup,
} from '@mui/material'
import { useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { NotificationsList, RemindersList } from './components'
import { NotificationIcon } from './icons'
import { CertificateNotification } from './modals'
import { styles } from './styles'
import { prepareNotifications } from './utils'
import { appStore, useAppDispatch, useAppSelector } from '@/store'

export const Notifications = () => {
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null)
  const [alignment, setAlignment] = useState<string>('1')

  const dispatch = useAppDispatch()
  const { employeesImportNotification } = useAppSelector(appStore)

  const handleOpen = (event: React.MouseEvent<HTMLButtonElement>) => {
    setAnchorEl(event.currentTarget)
  }
  const handleClose = () => {
    setAnchorEl(null)
  }
  const handleChange = (
    _: React.MouseEvent<HTMLElement>,
    newAlignment: string
  ) => {
    newAlignment && setAlignment(newAlignment)
  }

  const [getCertificatesExpiry] = useLazyGetCertificatesExpiryQuery()

  const navigate = useNavigate()
  const { showModal, hideModal, modalOpened } = useModal()

  const [notifications, setNotifications] = useState<CertificateCheckInfo[]>([])
  const [notificationQueue, setNotificationQueue] = useState<
    CertificateCheckInfo[]
  >([])
  const [notificationData, setNotificationData] =
    useState<CertificateCheckInfo>()

  const handleClear = (index: number) => {
    const newNotifications = [...notifications]
    const notification = newNotifications.splice(index, 1)[0]
    setNotifications(newNotifications)

    const skipNotifications = JSON.parse(
      localStorage.getItem('skip_notifications') ?? '[]'
    )
    localStorage.setItem(
      'skip_notifications',
      JSON.stringify([...skipNotifications, notification])
    )
  }
  const handleNavigate = (id?: number) => {
    if (id) {
      navigate(`/medcomplex/${id}/certificate`)
    }
  }
  const handleClearAll = () => {
    const skipNotifications = JSON.parse(
      localStorage.getItem('skip_notifications') ?? '[]'
    )
    localStorage.setItem(
      'skip_notifications',
      JSON.stringify([...skipNotifications, ...notifications])
    )
    setNotifications([])
  }

  const pastNotification = () => {
    const newQueue = [...notificationQueue]
    const notification = newQueue.splice(0, 1)[0]
    setNotificationQueue(newQueue)

    const pastNotifications = JSON.parse(
      localStorage.getItem('past_notifications') ?? '[]'
    )
    localStorage.setItem(
      'past_notifications',
      JSON.stringify([...pastNotifications, notification])
    )
  }

  const pastAllNotifications = () => {
    setNotificationQueue([])
    const pastNotifications = JSON.parse(
      localStorage.getItem('past_notifications') ?? '[]'
    )
    localStorage.setItem(
      'past_notifications',
      JSON.stringify([...pastNotifications, ...notificationQueue])
    )
  }

  useEffect(() => {
    ;(async () => {
      const data = await getCertificatesExpiry().unwrap()
      if (data) {
        const { notifications, notificationQueue } = prepareNotifications(
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
          data as any
        )
        setNotifications(notifications)
        setNotificationQueue(notificationQueue)
      }
    })()
  }, [])

  const timeOutDuration = 3000

  useEffect(() => {
    if (
      notificationQueue.length > 0 &&
      modalOpened === null &&
      anchorEl === null
    ) {
      setNotificationData(notificationQueue[0])
      showModal(ModalType.MEDCOMPLEX_CERTIFICATE_NOTIFICATION)

      // const timer = setTimeout(() => {
      //   pastNotification()
      //   hideModal()
      // }, timeOutDuration)
      // return () => clearTimeout(timer)
    }
  }, [
    notificationQueue,
    modalOpened,
    anchorEl,
    hideModal,
    showModal,
    pastNotification,
  ])

  useEffect(() => {
    if (notificationQueue.length > 0 && anchorEl !== null) {
      pastAllNotifications()
      hideModal()
    }
  }, [anchorEl])

  useEffect(() => {
    if (employeesImportNotification !== null) {
      setNotifications([
        ...notifications,
        {
          status: {
            slug: employeesImportNotification.text,
          },
          icon: employeesImportNotification.icon,
        },
      ])
      setNotificationQueue([
        ...notificationQueue,
        {
          status: {
            slug: employeesImportNotification.text,
          },
          icon: employeesImportNotification.icon,
        },
      ])
      dispatch(setEmployeesImportNotification(null))
    }
  }, [employeesImportNotification])

  return (
    <Box>
      <IconButton sx={{ width: '42px', height: '42px' }} onClick={handleOpen}>
        <Badge
          sx={styles.badge}
          color="secondary"
          badgeContent={notifications?.length}
        >
          <NotificationIcon />
        </Badge>
      </IconButton>
      <Popover
        sx={styles.popover}
        open={Boolean(anchorEl)}
        onClose={handleClose}
        anchorEl={anchorEl}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
        transformOrigin={{ vertical: 'top', horizontal: 'center' }}
      >
        <Stack>
          <Box>
            <ToggleButtonGroup
              sx={styles.toggleButtonsGroup}
              color="primary"
              value={alignment}
              exclusive
              onChange={handleChange}
            >
              <ToggleButton size={'large'} value={'1'}>
                Уведомления
              </ToggleButton>
              <ToggleButton size={'large'} value={'2'}>
                Напоминания
              </ToggleButton>
            </ToggleButtonGroup>
          </Box>
          <Box sx={styles.content}>
            {alignment === '1' && (
              <NotificationsList
                data={notifications ?? []}
                handleClear={handleClear}
                handleClearAll={handleClearAll}
                handleNavigate={handleNavigate}
              />
            )}
            {alignment === '2' && <RemindersList />}
          </Box>
        </Stack>
      </Popover>
      <CertificateNotification
        data={notificationData}
        pastNotification={pastNotification}
      />
    </Box>
  )
}
